import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon, Button } from "semantic-ui-react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchSettings, updateSettings } from "../settingsActions";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "./General.css";

const NotificationPreferences = ({
  settings,
  loading,
  updating,
  error,
  fetchSettings,
  updateSettings,
}) => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [weeklyReports, setWeeklyReports] = useState(true);

  const debounceTimeouts = useRef({});
  const editingFields = useRef(new Set());

  const debouncedUpdateSettings = useCallback(
    (fieldName, value, delay = 500) => {
      if (debounceTimeouts.current[fieldName]) {
        clearTimeout(debounceTimeouts.current[fieldName]);
      }

      debounceTimeouts.current[fieldName] = setTimeout(async () => {
        try {
          await updateSettings(fieldName, value);
          delete debounceTimeouts.current[fieldName];
          editingFields.current.delete(fieldName);
        } catch {
          delete debounceTimeouts.current[fieldName];
          editingFields.current.delete(fieldName);
        }
      }, delay);
    },
    [updateSettings]
  );

  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  useEffect(() => {
    if (!isSuspended) {
      fetchSettings();
    }
  }, [fetchSettings, isSuspended]);

  useEffect(() => {
    if (settings) {
      if (!editingFields.current.has("emailNotifications")) {
        setEmailNotifications(
          settings.isGetEmailNotifications !== undefined ? settings.isGetEmailNotifications : true
        );
      }
      if (!editingFields.current.has("pushNotifications")) {
        setPushNotifications(
          settings.isGetPushNotifications !== undefined ? settings.isGetPushNotifications : true
        );
      }
      if (!editingFields.current.has("weeklyReports")) {
        setWeeklyReports(
          settings.isGetWeeklyReports !== undefined ? settings.isGetWeeklyReports : true
        );
      }
    }
  }, [settings]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  const handleToggleChange = (field, value) => {
    editingFields.current.add(field);

    const fieldMapping = {
      emailNotifications: "IsGetEmailNotifications",
      pushNotifications: "IsGetPushNotifications",
      weeklyReports: "IsGetWeeklyReports",
    };

    switch (field) {
      case "emailNotifications":
        setEmailNotifications(value);
        break;
      case "pushNotifications":
        setPushNotifications(value);
        break;
      case "weeklyReports":
        setWeeklyReports(value);
        break;
      default:
        break;
    }

    if (fieldMapping[field]) {
      debouncedUpdateSettings(fieldMapping[field], value, 500);
    }
  };

  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Icon name="spinner" loading size="large" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: "center", padding: "50px", color: "#e53e3e" }}>
          <Icon name="exclamation triangle" size="large" />
          <p>
            {t("settings.general.errorLoadingSettings")} {error}
          </p>
          <Button onClick={() => fetchSettings()}>{t("settings.general.retry")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-settings">
      <div className="settings-section">
        <div className="settings-grid">
          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="mail outline" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.emailNotifications")}</h4>
                <p className="setting-description">
                  {t("settings.general.emailNotificationsDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={() => handleToggleChange("emailNotifications", !emailNotifications)}
                    disabled={updating}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="mobile alternate" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.pushNotifications")}</h4>
                <p className="setting-description">
                  {t("settings.general.pushNotificationsDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={pushNotifications}
                    onChange={() => handleToggleChange("pushNotifications", !pushNotifications)}
                    disabled={updating}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="chart line" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.weeklyReports")}</h4>
                <p className="setting-description">
                  {t("settings.general.weeklyReportsDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={weeklyReports}
                    onChange={() => handleToggleChange("weeklyReports", !weeklyReports)}
                    disabled={updating}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  settings: state.settingsReducer,
  loading: state.settingsReducer.loading,
  updating: state.settingsReducer.updating,
  error: state.settingsReducer.error,
});

const mapDispatchToProps = {
  fetchSettings,
  updateSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationPreferences);
